/**
 * Level Registry
 * 
 * Central module for importing and exporting all level configurations.
 * This modular approach allows for:
 * - Easy addition of new levels
 * - Individual level testing
 * - Better code organization
 * - Smaller bundle chunks (if using dynamic imports)
 * 
 * @module configs/levels
 */

import Level01_Meadow from './Level01_Meadow.js';
import Level02_Forest from './Level02_Forest.js';
import Level03_Cave from './Level03_Cave.js';
import Level04_Sky from './Level04_Sky.js';
import Level05_Volcano from './Level05_Volcano.js';
import Level06_Space from './Level06_Space.js';
import Level07_ZenSpa from './Level07_ZenSpa.js';
import Level08_NeonCity from './Level08_NeonCity.js';
import Level09_CrystalCavern from './Level09_CrystalCavern.js';
import Level10_TheSummit from './Level10_TheSummit.js';
import Level11_UnderwaterRuins from './Level11_UnderwaterRuins.js';
import Level12_CandyKingdom from './Level12_CandyKingdom.js';
import Level13_ClockworkTower from './Level13_ClockworkTower.js';
import Level14_AuroraBorealis from './Level14_AuroraBorealis.js';
import Level15_TheGauntlet from './Level15_TheGauntlet.js';
import Level16_EnchantedForest from './Level16_EnchantedForest.js';
import Level17_IronFortress from './Level17_IronFortress.js';
import Level18_VolcanicDepths from './Level18_VolcanicDepths.js';
import Level19_PrismaticSkyway from './Level19_PrismaticSkyway.js';
import Level20_TheNexus from './Level20_TheNexus.js';

/**
 * Array of all level configurations in order
 * @type {Array<LevelConfig>}
 */
const LEVELS = [
    Level01_Meadow,
    Level02_Forest,
    Level03_Cave,
    Level04_Sky,
    Level05_Volcano,
    Level06_Space,
    Level07_ZenSpa,
    Level08_NeonCity,
    Level09_CrystalCavern,
    Level10_TheSummit,
    Level11_UnderwaterRuins,
    Level12_CandyKingdom,
    Level13_ClockworkTower,
    Level14_AuroraBorealis,
    Level15_TheGauntlet,
    Level16_EnchantedForest,
    Level17_IronFortress,
    Level18_VolcanicDepths,
    Level19_PrismaticSkyway,
    Level20_TheNexus
];

/**
 * Total number of levels available
 * @type {number}
 */
export const TOTAL_LEVELS = LEVELS.length;

/**
 * Get a level by its ID (1-indexed)
 * @param {number} levelId - The level ID (1-10)
 * @returns {LevelConfig|undefined} The level configuration
 */
export const getLevelById = (levelId) => {
    return LEVELS.find(level => level.id === levelId);
};

/**
 * Get level by index (0-indexed)
 * @param {number} index - Array index
 * @returns {LevelConfig|undefined} The level configuration
 */
export const getLevelByIndex = (index) => {
    return LEVELS[index];
};

/**
 * Validate all levels have required properties
 * @returns {boolean} True if all levels are valid
 */
export const validateLevels = () => {
    const requiredProps = [
        'id', 'name', 'worldWidth', 'worldHeight', 
        'playerStart', 'theme', 'platforms', 'stars',
        'enemies', 'door', 'button', 'box', 'goal'
    ];
    
    return LEVELS.every(level => 
        requiredProps.every(prop => level.hasOwnProperty(prop))
    );
};

export default LEVELS;
